import type {
  AxiosResponse,
  AxiosError,
  AxiosRequestHeaders,
  AxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import { Notification } from '@arco-design/web-vue';
import { type Ref } from 'vue';
import { isAuthFailedCode } from '@/utils/auth';
import { getToken, clearToken } from '@/utils/auth';

export declare interface FetchOptions<DataT = any> extends AxiosRequestConfig<DataT> {
  method?: string,
  upload?: boolean,
  data?: DataT,
  json?: boolean,
  cache?: boolean,
  headers?: any,
  errorMessage?: string | boolean,
  loadingStatus?: Ref<boolean>,
  onUploadProgress?: (progress: any) => void,
  onUploadPercent?: (percent: number, progressEvent: any) => void,
}

export declare interface FetchResult<ResT> {
  isSuccess: boolean,
  code: number | string,
  msg?: string,
  data?: ResT,
  $response?: AxiosResponse | AxiosError | Error,
}

const DEFAULT_ERROR_CODE = 999999;

// 创建 axios 实例
const instance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  timeout: 30000,
});

// request 拦截器
instance.interceptors.request.use(config => {
  if (config.method === 'get') {
    config.params = config.data;
  }

  if (!config.headers) {
    config.headers = {} as AxiosRequestHeaders;
  }

  // 所有接口默认带 Authorization: Bearer，无 token 时为空
  config.headers.Authorization = `Bearer ${getToken()}`;

  return config;
}, error => {
  // Do something with request error
  console.error(error); // for debug
  return Promise.reject(error);
});

export async function $fetch<ResT = any, DataT = any>(url: string, inputOptions: FetchOptions<DataT> = {}): Promise<FetchResult<ResT>> {
  const options = { ...inputOptions };

  options.adapter = ['fetch', 'http', 'xhr'];
  options.fetchOptions = {
    credentials: 'omit',
  };

  if (!options.method) {
    options.method = 'post';
  }

  if (!options.data) {
    options.data = {} as DataT;
  }

  if (!options.headers) {
    options.headers = {};
  }

  if (options.upload === true) {
    const fd = new FormData();

    for (const key in options.data) {
      if (Object.prototype.hasOwnProperty.call(options.data, key)) {
        const value = options.data[key];

        if (value === undefined) {
          continue;
        }

        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (typeof item === 'object') {
              for (const subKey in item) {
                if (Object.prototype.hasOwnProperty.call(item, subKey)) {
                  fd.append(`${key}[${index}][${subKey}]`, item[subKey]);
                }
              }
            }
            else {
              fd.append(`${key}[]`, item);
            }
          });
        }
        else {
          fd.append(key, value as (string | Blob));
        }
      }
    }

    options.data = fd as unknown as DataT;
  }
  else if (options.method === 'post') {
    if (options.json !== false) {
      options.headers['Content-Type'] = 'application/json; charset=utf-8';
    }
    else {
      options.headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }
  }

  if (options.method.toLowerCase() === 'get' && options.cache !== true) {
    (options.data as { _: string })._ = String(+new Date()) + String(Math.floor(Math.random() * 9999));
  }

  if (!options.onUploadProgress && options.onUploadPercent) {
    options.onUploadProgress = (progressEvent) => {
      if (progressEvent.lengthComputable) {
        options.onUploadPercent?.(Math.floor((progressEvent.loaded / progressEvent.total) * 100), progressEvent);
      }
    }
  }

  let loadingTimer;

  if (options.loadingStatus) {
    loadingTimer = setTimeout(() => {
      options.loadingStatus && (options.loadingStatus.value = true);
    }, 200);
  }

  // 规范化 url：若携带 VITE_BASE_URL 前缀则去掉，保证 axios baseURL 不重复拼接
  const baseURL = import.meta.env.VITE_BASE_URL;

  if (baseURL && url.startsWith(baseURL)) {
    url = url.slice(baseURL.length) || '/';
  }

  let result: FetchResult<ResT>;

  try {
    const response = await instance(url, options);

    result = {
      isSuccess: response?.data?.success ?? true,
      code: response?.data?.code ?? 0,
      data: response?.data?.data,
      msg: response?.data?.message,
      $response: response,
    }
  }
  catch (error: any) {
    if (axios.isAxiosError(error)) {
      const response = error.response;

      result = {
        isSuccess: response?.data?.success ?? false,
        code: response?.data?.error?.code ?? DEFAULT_ERROR_CODE,
        data: response?.data?.error?.details,
        msg: response?.data?.error?.message || response?.data?.message || error.message,
        $response: response,
      }
    }
    else {
      result = {
        isSuccess: false,
        code: DEFAULT_ERROR_CODE,
        msg: error.message,
        $response: error,
      }
    }
  }

  if (options.loadingStatus) {
    clearTimeout(loadingTimer);
    options.loadingStatus.value = false;
  }

  if (!result.isSuccess) {
    if (import.meta.env.VITE_MOCK_LOGIN !== 'yes' && isAuthFailedCode(Number(result.code))) {
      clearToken();
      window.location.reload();
    }
    else {
      if (result.code === DEFAULT_ERROR_CODE) {
        Notification.error({
          content: `网络请求出错! API: ${url} ; 错误信息: ${result.msg}`,
          duration: 60 * 1000,
          closable: true,
        });
      }
      else if (options.errorMessage !== false) {
        Notification.error({
          content: (options.errorMessage as string) || result.msg || 'Error!',
          duration: 60 * 1000,
          closable: true,
        });
      }
    }
  }

  return result;
}
